import moment from "moment"

export const participantAge = (birthDate, year = new Date().getFullYear()) => {
  if (!birthDate) return null
  const date = moment.parseZone(birthDate, moment.ISO_8601, true)
  const age = year - date.year()
  return date.isValid() && age >= 0 ? age : null
}

export const participantLocation = location => {
  const parts = (location?.name || "").split(",").map(part => part.trim()).filter(Boolean)
  const country = location?.country || (parts.length > 1 ? parts[parts.length - 1] : "")
  const countryName = /^[a-z]{2}$/i.test(country) && typeof Intl.DisplayNames === "function"
    ? new Intl.DisplayNames(["en"], { type: "region" }).of(country.toUpperCase())
    : country
  return { city: location?.city || parts[0] || "Locality unavailable", country: countryName }
}

const validCoords = coords => Number.isFinite(coords?.lat) && Number.isFinite(coords?.lng) &&
  Math.abs(coords.lat) <= 90 && Math.abs(coords.lng) <= 180

// Match Planner uses the event GeoJSON point, falling back to the club coordinates.
export const distanceToEvent = (location, event) => {
  const [lng, lat] = event?.location?.coordinates || []
  const venue = validCoords({ lat, lng }) ? { lat, lng } : event?.club?.location?.coords
  const player = location?.coords
  if (!validCoords(player) || !validCoords(venue)) return null
  const radians = degrees => degrees * Math.PI / 180
  const a = Math.sin(radians(venue.lat - player.lat) / 2) ** 2 +
    Math.cos(radians(player.lat)) * Math.cos(radians(venue.lat)) *
    Math.sin(radians(venue.lng - player.lng) / 2) ** 2
  return Math.round(6371 * 2 * Math.asin(Math.sqrt(Math.min(1, Math.max(0, a)))))
}

// Old draw snapshots can omit contact/demographic fields present in the entry.
// Only fill those fields for the same player; never replace draw identity or seeding.
// A Tenis10 child (profileId) shares its parent's userId: the entry's phone, birth date and place are the parent's, never the child's
// (PRD 16 LR11b, register E39 (1)).
export const withEntryDetails = (person, entryPerson) => {
  if (person?.profileId) return person
  if (person?.userId == null || entryPerson?.userId == null || String(person.userId) !== String(entryPerson.userId)) return person
  return { ...person, birthDate: person.birthDate || entryPerson.birthDate,
    phone: person.phone || entryPerson.phone,
    location: person.location || entryPerson.location,
    w: person.w === true || entryPerson.w === true }
}

// P73 (Tudor 2026-10-29: „locația apare pe toate paginile”): a Tenis10 child's draw or match slot carries no place of its own — it takes
// the place of the child's competition entry (`child.location`, the Accounts hit the admin read adds to the lists), by the child's
// profileId, never a sibling's on the same parent account. An adult, or a child whose place is not known, stays as it is.
const ENTRY_LISTS = ["mainList", "waitingList", "preRegistrationList"]
export const withChildPlace = (person, competition) => {
  if (person?.profileId == null) return person
  const entry = ENTRY_LISTS.flatMap(list => competition?.[list] || [])
    .find(row => row?.profileId != null && String(row.profileId) === String(person.profileId) && row.child?.location)
  return entry ? { ...person, place: entry.child.location } : person
}

// A slot's link to its player's account: an adult's slot carries the account id, a Tenis10 child's slot does not (the parent's account is
// dropped from it, ADR-0003) — no link rather than `/accounts/players/undefined` (PRD 16 LR11b, register E39 (1)).
export const participantAccountPath = person => person?.id && !person.profileId ? `/accounts/players/${person.id}` : null
